import { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PageListContainer, PageCreateButton } from "../../../components/Page";
import { useConfirm } from "../../../components/feedback";
import DataTableWithPagination from "../../../components/DataTable";
import ProjectDialog from "../../../components/GenericDialog";
import { mailFilterCreateSchema, mailFilterEditSchema } from "../../../lib/validation/mailFilterSchemas";

import { mailFilterService } from "../../../services/mailFilterService";
import MailFilterModel, { getMailFilterCols } from "../model";
import createMailFilterBody from "./components/CreateMailFilterDialogBody";
import editMailFilterBody from "./components/EditMailFilterDialogBody";

function MailFiltersPage() {
  const { t } = useTranslation();
  const cols = useMemo(() => getMailFilterCols(t), [t]);
  const datatable = useRef<any>(null);
  const createDialogRef = useRef<any>(null);
  const editDialogRef = useRef<any>(null);

  const confirm = useConfirm();

  useEffect(() => {
    const handleGlobalRefresh = () => {
      if (datatable.current) datatable.current.refresh();
    };
    window.addEventListener("refreshDataTable", handleGlobalRefresh);
    return () => window.removeEventListener("refreshDataTable", handleGlobalRefresh);
  }, []);

  const showSnack = (opts: any) => {
    window.dispatchEvent(new CustomEvent("showGlobalNotification", { detail: opts }));
  };

  const createNewMailFilter = () => {
    if (createDialogRef.current) {
      createDialogRef.current.showDialog(MailFilterModel.createDialogOpts(t));
    }
  };

  const createMailFilterSubmitAction = (formJson: any) => {
    mailFilterService
      .createMailFilter({
        name: formJson.name,
        description: formJson.description || "",
        subject: formJson.subject,
        body: formJson.body,
        status: String(formJson.status) === "true",
      })
      .then(() => {
        MailFilterModel.message(
          MailFilterModel.SUCCESS,
          t("common.createRequest"),
          t("mailFilters.createdSuccess"),
          Date.now
        ).then((o) => {
          showSnack(o);
          setTimeout(() => datatable.current?.refresh(), 500);
        });
      })
      .catch((e: unknown) => console.error(e));
  };

  const editMailFilterSubmitAction = (formJson: any) => {
    mailFilterService
      .updateMailFilter({
        id: formJson.id,
        name: formJson.name,
        description: formJson.description || "",
        subject: formJson.subject,
        body: formJson.body,
        status: String(formJson.status) === "true",
      })
      .then(() => {
        MailFilterModel.message(
          MailFilterModel.SUCCESS,
          t("common.updateRequest"),
          t("mailFilters.updatedSuccess"),
          Date.now
        ).then((o) => {
          showSnack(o);
          setTimeout(() => datatable.current?.refresh(), 500);
        });
      })
      .catch((e: unknown) => console.error(e));
  };

  const handleDelete = async (item: any) => {
    return new Promise((resolve) => {
      confirm({ description: t("mailFilters.deleteConfirm", { name: item.name }) })
        .then(() => {
          mailFilterService.deleteMailFilter(item.id).then(() => resolve({}));
        })
        .then(() => {
          MailFilterModel.message(
            MailFilterModel.SUCCESS,
            t("common.deleteRequest"),
            t("mailFilters.deletedSuccess"),
            Date.now
          ).then((opts) => {
            showSnack(opts);
            setTimeout(() => datatable.current?.refresh(), 500);
          });
        })
        .catch((e: unknown) => console.error(e));
    });
  };

  const handleEdit = async (item: any) => {
    if (editDialogRef.current) {
      editDialogRef.current.showDialog(MailFilterModel.editDialogOpts(t), item);
    }
  };

  return (
    <PageListContainer>
      <ProjectDialog
        ref={createDialogRef}
        submitAction={createMailFilterSubmitAction}
        body={createMailFilterBody}
        validationSchema={mailFilterCreateSchema}
      />
      <ProjectDialog
        ref={editDialogRef}
        submitAction={editMailFilterSubmitAction}
        body={editMailFilterBody}
        validationSchema={mailFilterEditSchema}
      />

      <DataTableWithPagination
        ref={datatable}
        cols={cols}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        fetchUrl={"/mail-filter/read"}
        searchKey={"name"}
        headerActions={
          <PageCreateButton onClick={createNewMailFilter}>{t("mailFilters.new")}</PageCreateButton>
        }
      />
    </PageListContainer>
  );
}

export default MailFiltersPage;

